import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { useI18n } from "@/i18n";
import { getStorefrontImages, subscribeToMailingList } from "@/lib/api";

const collections = [
  { key: "original", image: "/images/original-category.jpg", setting: "original_category_image" },
  { key: "print", image: "/images/print-category.jpg", setting: "print_category_image" },
] as const;

export default function Home() {
  const { t, locale } = useI18n();
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [subscribed, setSubscribed] = useState(false);
  const [heroImage, setHeroImage] = useState("/images/hero-artwork-cropped.jpg");
  const [storefrontImages, setStorefrontImages] = useState<Record<string, string>>({});

  useEffect(() => {
    getStorefrontImages().then((images) => {
      setStorefrontImages(images);
      if (images.home_hero_image) setHeroImage(images.home_hero_image);
    }).catch(() => undefined);
  }, []);

  async function handleSubscribe(event: React.FormEvent) {
    event.preventDefault();
    setSubmitting(true);
    try {
      await subscribeToMailingList(email, locale);
      setSubscribed(true);
      setEmail("");
    } catch (error) {
      toast.error(error instanceof Error ? error.message : t.common.error);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="minimal-home">
      <section className="minimal-hero">
        <img
          src={heroImage}
          alt="Floral painting with two birds"
          className="minimal-hero-image"
        />
        <div className="hero-signature" aria-label="Dalila Di Maria">
          <img
            src="/images/logo-dalila-floral.png"
            alt="Dalila Di Maria"
            className="hero-signature-logo"
          />
        </div>
        <div className="minimal-hero-copy">
          <p>{t.home.heroSubtitle}</p>
          <Link to="/gallery" className="minimal-link">
            {t.home.heroCta}
            <ArrowRight size={15} />
          </Link>
        </div>
      </section>

      <section className="mailing-list-section" aria-labelledby="mailing-list-title">
        <h2 id="mailing-list-title">{t.home.mailingListTitle}</h2>
        <p>{t.home.mailingListText}</p>
        {subscribed ? <p className="mailing-list-success" role="status">{t.home.mailingListSuccess}</p> : (
          <form onSubmit={handleSubscribe} className="mailing-list-form">
            <label className="sr-only" htmlFor="mailing-list-email">{t.footer.emailPlaceholder}</label>
            <input
              id="mailing-list-email"
              type="email"
              required
              autoComplete="email"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              placeholder={t.footer.emailPlaceholder}
            />
            <button type="submit" disabled={submitting} aria-label={t.home.mailingListSubmit}>
              <ArrowRight size={25} />
            </button>
          </form>
        )}
      </section>

      <section className="minimal-collections">
        <div className="minimal-section-title">
          <span>{t.home.categoriesTitle}</span>
          <Link to="/gallery">{t.home.viewAll}</Link>
        </div>

        <div className="minimal-collection-grid">
          {collections.map((collection) => (
            <Link
              key={collection.key}
              to={`/gallery/${collection.key}`}
              className="minimal-collection-card"
          >
            <div className="minimal-collection-image">
              <img src={storefrontImages[collection.setting] || collection.image} alt={t.categories[collection.key]} />
              </div>
              <div className="minimal-collection-meta">
                <h2>{t.categories[collection.key]}</h2>
                <span>{t.categories[`${collection.key}Desc`]}</span>
              </div>
            </Link>
          ))}
        </div>
      </section>
    </div>
  );
}
